/**
 * Asset Statistics Service
 *
 * Fetches asset counts and statistics from the backend
 */

import assetService from '../api/assetService';

export interface LayerAssetStats {
  G: number; // Songs
  S: number; // Stars
  L: number; // Looks
  M: number; // Moves
  W: number; // Worlds
  B: number; // Branded
  P: number; // Personalize
  T: number; // Training_Data
  C: number; // Composites
  R: number; // Rights
  [key: string]: number; // Allow dynamic access
}

class AssetStatsService {
  private cache: LayerAssetStats | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 60000; // 1 minute cache

  /**
   * Get asset counts for all layers
   * Uses the new dedicated /api/assets/count endpoint for efficiency
   */
  async getLayerAssetCounts(): Promise<LayerAssetStats> {
    const now = Date.now();

    // Return cached data if it's still fresh
    if (this.cache && (now - this.cacheTimestamp) < this.CACHE_DURATION) {
      return this.cache;
    }

    try {
      // Use the new dedicated asset counts endpoint
      const response = await assetService.getAssetCounts();

      console.log('🔢 [ASSET STATS] Asset counts response:', response);

      // Handle different response structures from the API
      // The API might return the data directly or nested in a data property
      const countsData = (response as any)?.data?.byLayer || response?.byLayer || response;

      // Transform API response to LayerAssetStats interface
      const layerStats: LayerAssetStats = {
        G: countsData?.G || 0, // Songs
        S: countsData?.S || 0, // Stars
        L: countsData?.L || 0, // Looks
        M: countsData?.M || 0, // Moves
        W: countsData?.W || 0, // Worlds
        B: countsData?.B || 0, // Branded
        P: countsData?.P || 0, // Personalize
        T: countsData?.T || 0, // Training_Data
        C: countsData?.C || 0, // Composites
        R: countsData?.R || 0, // Rights
        // Include any additional layer codes from API response
        ...(countsData || {})
      };

      // Update cache
      this.cache = layerStats;
      this.cacheTimestamp = now;

      return layerStats;
    } catch (error) {
      console.error('❌ [ASSET STATS] Failed to fetch asset counts from new endpoint, falling back to old method:', error);

      // FALLBACK: Use the old method if new endpoint fails
      try {
        const layerCodes = ['G', 'S', 'L', 'M', 'W', 'B', 'P', 'T', 'C', 'R'];

        const countPromises = layerCodes.map(async (layer) => {
          try {
            const response = await assetService.getAssets({
              layer,
              limit: 1 // We only need the total count
            });
            return { layer, count: response.pagination?.total || 0 };
          } catch (error) {
            console.warn(`Failed to fetch count for layer ${layer}:`, error);
            return { layer, count: 0 };
          }
        });

        const results = await Promise.all(countPromises);

        // Build the stats object
        const stats: LayerAssetStats = {
          G: 0,
          S: 0,
          L: 0,
          M: 0,
          W: 0,
          B: 0,
          P: 0,
          T: 0,
          C: 0,
          R: 0,
        };

        results.forEach(({ layer, count }) => {
          stats[layer] = count;
        });

        // Update cache
        this.cache = stats;
        this.cacheTimestamp = now;

        return stats;
      } catch (fallbackError) {
        console.error('❌ [ASSET STATS] Fallback method also failed:', fallbackError);

        // Return default counts if both methods fail
        return {
          G: 0,
          S: 0,
          L: 0,
          M: 0,
          W: 0,
          B: 0,
          P: 0,
          T: 0,
          C: 0,
          R: 0,
        };
      }
    }
  }

  /**
   * Get asset count for a specific layer
   */
  async getLayerAssetCount(layer: string): Promise<number> {
    const stats = await this.getLayerAssetCounts();
    return stats[layer] || 0;
  }

  /**
   * Clear the cache to force a refresh
   */
  clearCache(): void {
    this.cache = null;
    this.cacheTimestamp = 0;
  }

  /**
   * Update cache when a new asset is created
   */
  incrementLayerCount(layer: string): void {
    if (this.cache && this.cache[layer] !== undefined) {
      this.cache[layer]++;
    }
  }
}

export const assetStatsService = new AssetStatsService();